/**
 * Viewport queries for layout that cannot be CSS-only.
 *
 * The recipe editor renders a card per ingredient on a phone and a dense grid
 * on desktop. Those are two DOM trees, not one restyled table — duplicate
 * labelled fields would fight screen readers and `autoFocus`. So we pick one
 * tree. No SSR, so the first paint can read `matchMedia` directly.
 */

import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

/** True from Tailwind `md` up — the recipe grid, not the phone cards. */
export function useDesktopEditor(): boolean {
  return useMediaQuery('(min-width: 768px)');
}
